<template>
  <div class="flex flex-col w-full h-full">
    <!-- Top section with search -->
    <div class="flex mb-4 w-full">
      <div class="field flex-grow-1">
        <label
          for="search"
          class="block text-sm font-medium mb-1"
        >Search Transport Types</label>
        <IconField class="w-full">
          <InputIcon class="pi pi-search" />
          <InputText
            id="search"
            v-model="searchQuery"
            placeholder="Search by name, type, or description..."
            class="w-full"
          />
        </IconField>
      </div>
    </div>

    <div class="flex flex-col md:flex-row w-full gap-3 flex-1 overflow-hidden">
      <!-- LEFT COLUMN: Transport Types List -->
      <div class="w-full md:w-2/3 mb-3 md:mb-0 overflow-hidden flex flex-col">
        <div class="border border-gray-200 rounded-lg shadow-sm h-full flex flex-col">
          <div class="p-3 pl-4 bg-gray-50 border-b border-gray-200">
            <h3 class="text-lg font-semibold m-0">
              Available Transport Types
            </h3>
          </div>
          <div class="overflow-auto flex-1">
            <DataTable
              :value="filteredItems"
              sort-field="name"
              :row-style="rowStyle"
              :row-class="rowClass"
              striped-rows
              :loading="isLoading"
              class="p-datatable-sm"
              style="min-width: 100%"
              @row-click="onRowClick"
            >
              <template #empty>
                <DataTableEmptyState
                  :is-loading="isLoading"
                  :error="error"
                  loading-message="Loading transport types..."
                  empty-message="No transport types match your criteria."
                  empty-hint="Try adjusting your search."
                  empty-icon="pi pi-truck"
                />
              </template>
              <Column
                field="name"
                sortable
              >
                <template #header>
                  <div class="pl-3">
                    Transport Type
                  </div>
                </template>
                <template #body="{ data }">
                  <div class="pl-3">
                    <span class="font-medium">{{ data.name }}</span>
                  </div>
                </template>
              </Column>
              <Column
                field="rate"
                sortable
                style="width: 120px"
              >
                <template #header>
                  <div class="pl-3">
                    Day Rate
                  </div>
                </template>
                <template #body="{ data }">
                  <div class="text-right pl-3">
                    <input
                      type="number"
                      :value="data.rate"
                      :min="0"
                      :step="0.01"
                      class="w-full px-2 py-1 text-right border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style="width: 100px;"
                      @input="(event) => updateRate(data, event.target.value)"
                    >
                  </div>
                </template>
              </Column>
              <Column
                style="width: 80px"
              >
                <template #header>
                  <div class="pl-3">
                    Qty
                  </div>
                </template>
                <template #body="{ data }">
                  <div class="flex items-center justify-center pl-3">
                    <input
                      type="number"
                      :value="data.qtyToAdd || 0"
                      :min="0"
                      :max="10"
                      class="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style="width: 60px;"
                      @input="(event) => selectRow(data, event.target.value)"
                    >
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: Selected Transport Panel -->
      <SelectionBasketPanel
        title="Selected Transport"
        :items="selectedTransport"
        :total-count="getSelectedTransportCount()"
        :formatted-total-amount="formatCurrency(calculateTotal())"
        total-count-label="Total Transport"
        total-amount-label="Total Cost"
        :view-mode="selectedViewMode"
        :is-submitting="isSubmitting"
        empty-message="No transport selected"
        empty-hint="Select transport from the table to add them here"
        @clear="clearSelectedTransport"
        @toggle-view="toggleSelectedViewMode"
        @save-and-close="saveTransportAndClose"
        @save-and-keep-open="saveTransportAndKeepOpen"
        @remove="removeSelectedTransport"
      >
        <template #detailed="{ item: transport, remove }">
          <div class="flex align-items-center justify-between mb-3">
            <div class="font-medium">
              {{ transport.name }}
            </div>
            <Button
              icon="pi pi-times"
              text
              rounded
              severity="danger"
              class="p-button-sm"
              @click="remove"
            />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="field">
              <label class="block text-xs font-medium text-gray-700 mb-1">Day Rate</label>
              <InputNumber
                v-model="transport.rate"
                :min="0"
                :max="999999"
                mode="decimal"
                :min-fraction-digits="2"
                class="w-full"
                size="small"
              />
            </div>
            <div class="field">
              <label class="block text-xs font-medium text-gray-700 mb-1">Qty</label>
              <InputNumber
                v-model="transport.qtyToAdd"
                :min="0"
                :max="999"
                class="w-full"
                size="small"
              />
            </div>
          </div>
          <div class="mt-3 pt-2 border-t border-gray-100">
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium">Total:</span>
              <span class="font-bold text-green-600">
                {{ formatCurrency((transport.qtyToAdd || 0) * (transport.rate || 0)) }}
              </span>
            </div>
          </div>
        </template>
      </SelectionBasketPanel>
    </div>

    <!-- Bottom Action -->
    <div class="flex justify-end mt-3">
      <Button
        label="Cancel"
        icon="pi pi-times"
        text
        @click="closeDialog"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, defineEmits } from 'vue';
import { getTransportTypes } from '@/services/transportTypesService';
import { formatCurrency } from '@/composables/formatCurrency';
import { projectStore } from '../projectStore';
import { useToastService } from '@/composables/useToastService';
import SelectionBasketPanel from '@/components/shared/SelectionBasketPanel.vue';
import DataTableEmptyState from '@/components/shared/DataTableEmptyState.vue';

// Toast service
const toast = useToastService();

// Store the items returned by getTransportTypes
const items = ref([]);
const selectedTransport = ref([]);

// Initialize items on component mount
onMounted(async () => {
    try {
        isLoading.value = true;
        error.value = null;
        const response = await getTransportTypes();
        const data = response.data || response;
        
        items.value = data.map(transportType => {
            const mappedItem = {
                ...transportType,
                // Extract rate from pivot object if it exists, otherwise use the direct rate
                rate: transportType.pivot?.rate || transportType.rate || 0,
                qtyToAdd: 0 // Initialize quantity for each item
            };
            return mappedItem;
        });
        filteredItems.value = items.value;
    } catch (err) {
        console.error('[AddLogisticsModal] Error fetching transport types:', err);
        error.value = 'Failed to load transport types. Please try again.';
        items.value = [];
        filteredItems.value = [];
    } finally {
        isLoading.value = false;
    }
});

// State variables
const searchQuery = ref('');
const selectedViewMode = ref('simple'); // 'detailed' | 'simple'
const filteredItems = ref([]);
const isLoading = ref(false);
const error = ref(null);
const isSubmitting = ref(false);

// Watch for search query changes
watch(searchQuery, (newQuery) => {
    if (!newQuery) {
        filteredItems.value = items.value;
    } else {
        const query = newQuery.toLowerCase();
        filteredItems.value = items.value.filter(item => 
            item.name.toLowerCase().includes(query)
        );
    }
});

// Function to handle row clicks in the DataTable
const onRowClick = (event) => {
    // Set default quantity to 1 if not already set
    if (!event.data.qtyToAdd) {
        event.data.qtyToAdd = 1;
    }
    
    // Select the row
    selectRow(event.data, event.data.qtyToAdd);
};

// Function to select a row and add to selected transport
const selectRow = (transport, qty) => {
    const qtyNum = parseInt(qty) || 0;
    
    if (qtyNum > 0) {
        // Check if transport is already selected
        const existingIndex = selectedTransport.value.findIndex(t => t.id === transport.id);
        
        if (existingIndex >= 0) {
            // Update existing transport quantity
            selectedTransport.value[existingIndex].qtyToAdd = qtyNum;
        } else {
            // Add new transport to selection
            selectedTransport.value.push({
                ...transport,
                qtyToAdd: qtyNum
            });
        }
    } else {
        // Remove transport from selection if quantity is 0
        selectedTransport.value = selectedTransport.value.filter(t => t.id !== transport.id);
    }
    
    // Update the transport item in filteredItems
    transport.qtyToAdd = qtyNum;
};

// Function to remove selected transport
const removeSelectedTransport = (transport) => {
    selectedTransport.value = selectedTransport.value.filter(t => t.id !== transport.id);
    
    // Also reset the qtyToAdd in the filteredItems
    const item = filteredItems.value.find(item => item.id === transport.id);
    if (item) {
        item.qtyToAdd = 0;
    }
};

// Toggle between detailed and simple view for selected transport
const toggleSelectedViewMode = () => {
    selectedViewMode.value = selectedViewMode.value === 'detailed' ? 'simple' : 'detailed';
};

// Perform save to API + refresh + success toast (no close/clear)
const performSave = async () => {
    const projectId = projectStore.currentProjectId;
    const equipmentListId = projectStore.currentEquipmentListId;
    const parentGroupId = projectStore.currentDraggableItemId;

    if (!projectId || !equipmentListId) {
        throw new Error('Project ID or Equipment List ID not found');
    }

    const { addEquipmentListContent } = await import('@/services/projectService');
    const promises = selectedTransport.value.map(async (transport) => {
        const contentData = {
            type: "item",
            name: transport.name,
            asset_type: "transport",
            quantity: transport.qtyToAdd,
            days: 3,
            price: transport.rate,
            discount: 0,
            notes: "",
            parent_id: parentGroupId ? parseInt(parentGroupId) : null,
            additional_data: {
                transport_type_id: transport.id,
                rate_type: "fixed",
                rate: transport.rate,
                description: transport.description || `${transport.name} for project transport needs`
            }
        };
        return await addEquipmentListContent(projectId, equipmentListId, contentData);
    });

    await Promise.all(promises);

    if (projectStore.refreshFunction && typeof projectStore.refreshFunction === 'function') {
        await projectStore.refreshFunction();
    }

    toast.add({
        severity: 'success',
        summary: 'Transport Added',
        detail: `${getSelectedTransportCount()} transport items added to the project`,
        life: 3000
    });
};

// Save basket and close (same as Add to Project)
const saveTransportAndClose = () => {
    saveTransport();
};

// Save basket and keep window open with items emptied
const saveTransportAndKeepOpen = async () => {
    if (isSubmitting.value || !selectedTransport.value?.length) return;
    isSubmitting.value = true;
    try {
        await performSave();
        emit('transportSubmit', selectedTransport.value);
        clearSelectedTransport();
    } catch (err) {
        console.error('[AddLogisticsModal] Error adding transport:', err);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: err.response?.data?.message || err.message || 'Failed to add transport. Please try again.',
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
};

// Function to clear all selected transport
const clearSelectedTransport = () => {
    // Reset quantities in the table
    filteredItems.value.forEach(item => {
        if (item.qtyToAdd) item.qtyToAdd = 0;
    });

    // Clear the selected transport array
    selectedTransport.value = [];
};

// Get total selected transport count
const getSelectedTransportCount = () => {
    return selectedTransport.value.reduce((total, transport) => total + transport.qtyToAdd, 0);
};

// Calculate total cost
const calculateTotal = () => {
    const total = selectedTransport.value.reduce((total, transport) => {
        const qty = transport.qtyToAdd || 0;
        const rate = transport.rate || 0;
        return total + (qty * rate);
    }, 0);
    
    // Ensure the total is rounded to 2 decimal places to avoid floating-point precision issues
    return Math.round(total * 100) / 100;
};

// Function to save transport to project and close
const saveTransport = async () => {
    if (isSubmitting.value || !selectedTransport.value?.length) return;
    isSubmitting.value = true;
    try {
        await performSave();
        emit('transportSubmit', selectedTransport.value);
        closeDialog();
    } catch (err) {
        console.error('[AddLogisticsModal] Error adding transport:', err);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: err.response?.data?.message || err.message || 'Failed to add transport. Please try again.',
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
};

// Function to close the dialog
const closeDialog = () => {
    // Reset state
    selectedTransport.value = [];
    searchQuery.value = '';
    filteredItems.value = items.value;
    
    // Emit close event
    emit('close');
};

// Function to update rate
const updateRate = (transport, newRate) => {
    const rate = parseFloat(newRate) || 0;
    transport.rate = rate;
    
    // Update the corresponding selected transport if it exists
    const selectedTransportItem = selectedTransport.value.find(t => t.id === transport.id);
    if (selectedTransportItem) {
        selectedTransportItem.rate = rate;
    }
};

// Function to style rows
const rowStyle = (data) => {
    return data.qtyToAdd > 0 ? { backgroundColor: '#E3F2FD' } : {};
};

const rowClass = () => {
    return {
        'cursor-pointer hover:bg-gray-50': true
    };
};

// Define emits
const emit = defineEmits(['transportSubmit', 'close']);
</script>

<style scoped>
/* Style the data table */
:deep(.p-datatable-sm) {
    font-size: 0.875rem;
}

:deep(.p-datatable .p-datatable-tbody > tr) {
    cursor: pointer;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
    background-color: var(--surface-50);
}
</style>
